import { useTranslation } from 'react-i18next';
import type { OrganizationProfile } from '@hero/schema';
import {
  CarrierBankingForm,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@hero/web-components';
import { BankIcon } from '@hero/web-icons';

export interface CarrierBankingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  profile: OrganizationProfile | undefined;
  onSaved?: () => void;
}

/**
 * `CarrierBankingDrawer` — financing-flow drawer that wraps the
 * shared `CarrierBankingForm` with the financing-side header and
 * deposit-destination callout.
 *
 * The form itself owns its RHF state, validation, and the
 * Save mutation (via `useOrgProfileUpdateApi`). The drawer's only
 * extra responsibility is to close itself on success and provide
 * the financing-themed surrounding chrome (Sheet, callout, copy).
 *
 * Passing `displayFor="financing"` decorates every bank field
 * with the red financing-required chip — same fields, different
 * presentation than the carrier-dashboard Profile → Banking tab.
 */
export function CarrierBankingDrawer({
  isOpen,
  onClose,
  profile,
  onSaved,
}: CarrierBankingDrawerProps) {
  const { t } = useTranslation(['m-financing']);

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle>{t('m-financing:requirements.carrier_banking.title')}</SheetTitle>
          <SheetDescription>
            {t('m-financing:requirements.carrier_banking.description')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 p-6">
          {/* Deposit-destination callout. Sits directly above the
              inputs so the "why we're asking" is right next to the
              "what we're asking for." Emerald matches the financing
              module's primary palette. */}
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <BankIcon className="size-4" />
            </span>
            <p className="text-sm leading-relaxed text-emerald-900">
              {t('m-financing:requirements.carrier_banking.deposit_callout')}
            </p>
          </div>

          <CarrierBankingForm
            profile={profile}
            displayFor="financing"
            onSaved={() => {
              onSaved?.();
              onClose();
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
